import React, { memo } from 'react'
import { Maybe } from '@pylot-data/fwrdschema'
import detectPageBuilder from './PageBuilder/detectPageBuilder'
import PageBuilder from './PageBuilder'
import PageBuilderExtend from './PageBuilder/PageBuilderExtend'
import { mergeClasses } from './PageBuilder/classify'
import defaultClasses from './richContent.module.scss'
import { useAnchorNavigation } from './PageBuilder/useAnchorNavigation'
import { prepareCmsMediaHtml } from './PageBuilder/applyInlineMobileVideoPlayback'
import PageBuilderStyleTags from './PageBuilder/PageBuilderStyleTags'
import { normalizePageBuilderHtml } from './PageBuilder/utils'

export type RichContentClasses = {
    root?: string
}

export type ImageNavigationCallback = (
    linkedCategory: string | null | undefined
) => void

export interface RichContentProps extends Record<string, unknown> {
    html?: Maybe<string>
    /** Pre-parsed PageBuilder tree from getStaticProps (avoids jsdom vs DOMParser hydration mismatch) */
    pageBuilderData?: Record<string, unknown> | null
    classes?: RichContentClasses
    extend?: boolean
    config?: Record<string, unknown>
    isLazyLoad?: boolean
    ProductTile?: React.ElementType
    productQuery?: unknown
    extendedContentTypeConfig?: unknown
    imageNavCallback?: ImageNavigationCallback
}

type PlainHtmlComponentProps = {
    html?: Maybe<string>
    classes: RichContentClasses
}

const toHTML = (str: Maybe<string>): { __html: string } => ({
    __html: str ?? ''
})

const PlainHtmlComponent = ({ html, classes }: PlainHtmlComponentProps) => {
    const anchorNavigationRef = useAnchorNavigation as unknown as (
        content?: string | null
    ) => React.MutableRefObject<HTMLDivElement | null>

    const { html: cleanHtml, cssBlocks } = normalizePageBuilderHtml(html ?? '')
    const preparedHtml = prepareCmsMediaHtml(cleanHtml)
    const wrapperRef = anchorNavigationRef(preparedHtml || null)

    return (
        <>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <PageBuilderStyleTags cssBlocks={cssBlocks} idPrefix="pb-plain" />
            {preparedHtml ? (
                <div
                    ref={wrapperRef}
                    className={classes.root}
                    dangerouslySetInnerHTML={toHTML(preparedHtml)}
                />
            ) : null}
        </>
    )
}

const RichContent = (props: RichContentProps) => {
    const {
        html,
        pageBuilderData,
        imageNavCallback,
        config,
        extend,
        isLazyLoad = true,
        ProductTile,
        productQuery,
        extendedContentTypeConfig
    } = props

    const classes = mergeClasses(
        defaultClasses,
        props.classes
    ) as RichContentClasses
    const PageBuilderComponent = PageBuilder as unknown as React.ComponentType<
        Record<string, unknown>
    >
    const PageBuilderExtendComponent =
        PageBuilderExtend as unknown as React.ComponentType<
            Record<string, unknown>
        >

    if (detectPageBuilder(html ?? '')) {
        if (extend) {
            return (
                <div className={classes.root}>
                    <PageBuilderExtendComponent
                        masterFormat={html}
                        contentType={config}
                        isLazyLoad={isLazyLoad}
                        ProductTile={ProductTile}
                        productQuery={productQuery}
                        extendedContentTypeConfig={extendedContentTypeConfig}
                        imageNavCallback={imageNavCallback}
                    />
                </div>
            )
        }

        return (
            <div className={classes.root}>
                <PageBuilderComponent
                    masterFormat={html}
                    data={pageBuilderData}
                    isLazyLoad={isLazyLoad}
                    ProductTile={ProductTile}
                    productQuery={productQuery}
                    imageNavCallback={imageNavCallback}
                />
            </div>
        )
    }

    return <PlainHtmlComponent classes={classes} html={html ?? null} />
}

// Keep React.memo default comparison (Object.is per prop).
// Avoid JSON.stringify comparisons because props can include circular references.
export default memo(RichContent)
